import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Container,
  Heading,
  HStack,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CheckCircleIcon, InfoIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useStreamingSTT } from "../../hooks/useStreamingSTT";

const RESULT_IDLE = "idle";
const RESULT_TESTING = "testing";
const RESULT_OK = "ok";
const RESULT_FAIL = "fail";

const MicCheckPage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { testConnection } = useStreamingSTT(uuid);

  const [status, setStatus] = useState(RESULT_IDLE);
  const [errorMessage, setErrorMessage] = useState(null);

  const runTest = async () => {
    setStatus(RESULT_TESTING);
    setErrorMessage(null);
    const lang = (i18n.language || "en").startsWith("fr") ? "fr-FR" : "en-US";
    const result = await testConnection({ durationMs: 3000, language: lang });
    if (result && result.success) {
      setStatus(RESULT_OK);
    } else {
      setStatus(RESULT_FAIL);
      setErrorMessage((result && result.error) || null);
    }
  };

  const goToSample = () => navigate(`/evaluate/${uuid}/sample`);

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.md">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={6} shadow="sm">
          <VStack spacing={5} align="stretch">
            <Heading size="lg" color="black" display="flex" alignItems="center" gap={2}>
              <InfoIcon color="blue.500" /> {t("evaluation.micCheck.title")}
            </Heading>

            <Text fontSize="sm" color="gray.700" data-testid="mic-check-instructions">
              {t("evaluation.micCheck.instructions")}
            </Text>

            {status === RESULT_IDLE && (
              <Button
                colorScheme="blue"
                size="lg"
                onClick={runTest}
                data-testid="mic-check-start"
              >
                {t("evaluation.micCheck.testButton")}
              </Button>
            )}

            {status === RESULT_TESTING && (
              <HStack spacing={3} data-testid="mic-check-testing">
                <Spinner color="blue.500" />
                <Text>{t("evaluation.micCheck.testing")}</Text>
              </HStack>
            )}

            {status === RESULT_OK && (
              <Alert
                status="success"
                borderRadius="md"
                data-testid="mic-check-success"
                role="status"
              >
                <AlertIcon as={CheckCircleIcon} />
                <Box>
                  <Text fontWeight="bold">
                    {t("evaluation.micCheck.successTitle")}
                  </Text>
                  <Text fontSize="sm">{t("evaluation.micCheck.successMessage")}</Text>
                </Box>
              </Alert>
            )}

            {status === RESULT_FAIL && (
              <Alert
                status="error"
                borderRadius="md"
                alignItems="flex-start"
                data-testid="mic-check-failure"
                role="alert"
              >
                <AlertIcon as={WarningTwoIcon} />
                <Box>
                  <Text fontWeight="bold">
                    {t("evaluation.micCheck.failureTitle")}
                  </Text>
                  <Text fontSize="sm" mb={2}>
                    {t("evaluation.micCheck.failureMessage")}
                  </Text>
                  {errorMessage && (
                    <Text fontSize="xs" color="red.700" mb={2}>
                      {errorMessage}
                    </Text>
                  )}
                  <Text fontSize="sm" fontWeight="semibold" mt={1}>
                    {t("evaluation.micCheck.troubleshooting")}
                  </Text>
                  <List spacing={1} fontSize="sm" mt={1}>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      {t("evaluation.micCheck.tipPermission")}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      {t("evaluation.micCheck.tipDevice")}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      {t("evaluation.micCheck.tipNetwork")}
                    </ListItem>
                  </List>
                </Box>
              </Alert>
            )}

            <ButtonGroup spacing={3} justifyContent="flex-end" display="flex" flexWrap="wrap">
              {status === RESULT_FAIL && (
                <Button variant="outline" onClick={runTest} data-testid="mic-check-retry">
                  {t("evaluation.micCheck.retry")}
                </Button>
              )}
              {status === RESULT_OK && (
                <Button
                  colorScheme="blue"
                  onClick={goToSample}
                  data-testid="mic-check-continue"
                >
                  {t("evaluation.micCheck.continue")}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={goToSample}
                data-testid="mic-check-skip"
              >
                {t("evaluation.micCheck.continueWithout")}
              </Button>
            </ButtonGroup>

            {status !== RESULT_OK && (
              <Text fontSize="xs" color="gray.500">
                {t("evaluation.micCheck.skipExplanation")}
              </Text>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default MicCheckPage;
